import React from 'react';
import { useParams } from 'react-router-dom';
import { Shield } from 'lucide-react';

const pages = {
  privacy: {
    title: 'Privacy Policy',
    effective: 'Effective Date: January 1, 2021',
    content: `
      <h2>1. Introduction</h2>
      <p>MaTaxPreparer LLC ("Company," "we," "us," or "our") is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our services.</p>

      <h2>2. Information We Collect</h2>
      <p>We collect information you provide directly, including:</p>
      <ul>
        <li>Personal identification information (name, address, Social Security Number)</li>
        <li>Financial information (income, tax documents, bank account details)</li>
        <li>Contact information (email, phone number)</li>
        <li>Account credentials and preferences</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use collected information to:</p>
      <ul>
        <li>Prepare and file your tax returns</li>
        <li>Communicate with you about your tax matters</li>
        <li>Process payments for our services</li>
        <li>Improve our services and platform</li>
        <li>Comply with IRS and legal requirements</li>
      </ul>

      <h2>4. Information Sharing</h2>
      <p>We do not sell, trade, or rent your personal information to third parties. We may share information:</p>
      <ul>
        <li>With the IRS and relevant tax authorities as required for filing</li>
        <li>With service providers who assist our operations under strict confidentiality agreements</li>
        <li>When required by law or legal process</li>
      </ul>

      <h2>5. Data Security</h2>
      <p>We implement industry-standard security measures including:</p>
      <ul>
        <li>256-bit SSL encryption for data transmission</li>
        <li>Encrypted storage for all sensitive documents</li>
        <li>Strict access controls and audit logging</li>
        <li>IRS Publication 4557 data security compliance</li>
      </ul>

      <h2>6. Data Retention</h2>
      <p>We retain your tax information for a minimum of 7 years in accordance with IRS requirements and Georgia state law.</p>

      <h2>7. Your Rights</h2>
      <p>You have the right to access, correct, or request deletion of your personal information. Contact us at mataxpreparerllc@yahoo.com to exercise these rights.</p>

      <h2>8. Contact Us</h2>
      <p>For privacy concerns, contact: MaTaxPreparer LLC, 4384 Laughlin Ct NW, Kennesaw, GA 30144 | 770-235-6999 | mataxpreparerllc@yahoo.com</p>
    `
  },
  terms: {
    title: 'Terms of Service',
    effective: 'Effective Date: January 1, 2021',
    content: `
      <h2>1. Acceptance of Terms</h2>
      <p>By engaging MaTaxPreparer LLC for tax preparation services, you agree to these Terms of Service. If you do not agree, do not use our services.</p>

      <h2>2. Services Provided</h2>
      <p>MaTaxPreparer LLC provides tax preparation, filing, and advisory services. Services are rendered based on information provided by the client.</p>

      <h2>3. Client Responsibilities</h2>
      <p>You agree to:</p>
      <ul>
        <li>Provide accurate, complete, and timely information</li>
        <li>Review your completed return before signing</li>
        <li>Pay agreed-upon fees for services rendered</li>
        <li>Maintain confidentiality of your portal credentials</li>
      </ul>

      <h2>4. No Guarantee of Outcomes</h2>
      <p>We do not guarantee specific refund amounts, audit outcomes, or tax savings. Our commitment is to accurate, lawful preparation of your return based on current tax law and the information you provide.</p>

      <h2>5. Fees and Payment</h2>
      <p>Service fees are communicated in advance. Payment is due upon completion of your return. Unpaid accounts may result in withholding of completed returns.</p>

      <h2>6. Limitation of Liability</h2>
      <p>Our liability is limited to the fees paid for the specific service in question. We are not liable for penalties arising from inaccurate client-provided information.</p>

      <h2>7. Governing Law</h2>
      <p>These terms are governed by the laws of the State of Georgia. Disputes shall be resolved in Cobb County, Georgia courts.</p>

      <h2>8. Modifications</h2>
      <p>We reserve the right to modify these terms with notice. Continued use of services constitutes acceptance of modifications.</p>
    `
  },
  'circular-230': {
    title: 'IRS Circular 230 Disclosure',
    effective: 'Required by IRS Regulations',
    content: `
      <h2>Circular 230 Disclosure</h2>
      <p>In accordance with requirements of the Treasury Department's Circular 230, we are required to notify you that any tax advice contained in this communication, including any attachments, was not intended or written to be used, and cannot be used, for the purpose of avoiding tax penalties that may be imposed by the Internal Revenue Service.</p>

      <h2>About Our Credentials</h2>
      <p>MaTaxPreparer LLC operates under Fajlur Rahman, PTP, CPE, an IRS-authorized tax professional holding a valid Preparer Tax Identification Number (PTIN) as required by IRS regulations.</p>

      <h2>Practice Before the IRS</h2>
      <p>Our practice before the IRS includes preparation and signing of tax returns, representation of clients during audits and appeals, and assistance with IRS correspondence and notices.</p>

      <h2>Standards of Conduct</h2>
      <p>We adhere to the standards of conduct set forth in IRS Circular 230, including:</p>
      <ul>
        <li>Competence and diligence in preparation of returns</li>
        <li>Prohibition against representing clients before the IRS when conflicts of interest exist</li>
        <li>Requirements to advise clients of errors on prior returns</li>
        <li>Standards for written advice on federal tax matters</li>
      </ul>

      <h2>No Guarantee</h2>
      <p>No tax professional may guarantee the outcome of any IRS proceeding. We commit to providing our best professional judgment based on applicable law and the information you provide.</p>

      <h2>Contact</h2>
      <p>MaTaxPreparer LLC | 4384 Laughlin Ct NW, Kennesaw, GA 30144 | 770-235-6999 | mataxpreparerllc@yahoo.com</p>
    `
  }
};

const LegalPage = ({ page: pageProp }) => {
  const params = useParams();
  const pageType = pageProp || params.type || 'privacy';
  const page = pages[pageType] || pages.privacy;

  return (
    <div style={{ paddingTop: '72px' }}>
      <section className="py-14 px-6" style={{ background: '#0B1F3A' }}>
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-2 mb-2">
            <Shield size={16} style={{ color: '#D4AF37' }} />
            <span className="label-uppercase" style={{ color: '#D4AF37' }}>Legal</span>
          </div>
          <h1 className="font-heading text-4xl font-bold text-white tracking-tight mb-2">{page.title}</h1>
          <p className="text-blue-300 text-sm">{page.effective}</p>
        </div>
      </section>

      <section className="py-14 px-6 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10">
            <p className="text-amber-800 text-sm font-medium">
              Governing Law: State of Georgia | MaTaxPreparer LLC | IRS Authorized E-File Provider
            </p>
          </div>
          <div
            className="prose prose-base max-w-none legal-content"
            style={{ color: '#0F172A' }}
            dangerouslySetInnerHTML={{ __html: page.content }}
          />
          <style>{`
            .legal-content h2 { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 1.25rem; font-weight: 700; color: #0B1F3A; margin-top: 2rem; margin-bottom: 0.75rem; }
            .legal-content p { color: #475569; line-height: 1.75; margin-bottom: 1rem; }
            .legal-content ul { list-style-type: disc; padding-left: 1.5rem; color: #475569; line-height: 1.75; }
            .legal-content li { margin-bottom: 0.4rem; }
          `}</style>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
